import React from 'react';
import { Box, Container, Typography, Grid, Button, Card, CardContent, Avatar } from '@mui/material';
import { motion } from 'framer-motion';
import Hero from '../components/Hero';
import Features from '../components/Features';
import PricingPlans from '../components/PricingPlans';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import EngineeringIcon from '@mui/icons-material/Engineering';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import SpeedIcon from '@mui/icons-material/Speed';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

const testimonials = [
  {
    quote: "MekaSys ha transformado completamente la gestión de nuestro taller. Ahora podemos atender más clientes en menos tiempo y con mejor calidad.",
    author: "Carlos Gutiérrez",
    role: "Gerente de Taller Automotriz Central",
  },
  {
    quote: "La facilidad para registrar los vehículos con fotos y generar presupuestos profesionales ha mejorado nuestra imagen frente a los clientes.",
    author: "Ana Martínez",
    role: "Propietaria de Servitec Automotriz",
  },
  {
    quote: "El seguimiento de cada etapa de servicio nos permite tener todo bajo control. Los clientes valoran mucho recibir actualizaciones sobre el estado de sus vehículos.",
    author: "Roberto Sánchez",
    role: "Director de Operaciones, MotorPro",
  },
];

const benefits = [
  {
    title: 'Mejora la eficiencia',
    description: 'Automatiza procesos manuales y reduce el tiempo dedicado a tareas administrativas.',
    icon: SpeedIcon,
    color: '#4caf50',
  },
  {
    title: 'Aumenta la satisfacción',
    description: 'Ofrece una mejor experiencia a tus clientes con comunicación clara y servicios organizados.',
    icon: AssignmentTurnedInIcon,
    color: '#f50057',
  },
  {
    title: 'Optimiza recursos',
    description: 'Gestiona de manera eficiente el tiempo de tus mecánicos y el inventario de repuestos.',
    icon: BuildCircleIcon,
    color: '#ff9800',
  },
  {
    title: 'Potencia tu negocio',
    description: 'Toma decisiones basadas en datos con reportes detallados y análisis de rendimiento.',
    icon: EngineeringIcon,
    color: '#1976d2',
  },
];

const HomePage = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Hero />

      {/* Beneficios */}
      <Box sx={{ py: 10, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              component={motion.div}
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              variant="overline"
              color="primary"
              sx={{ fontWeight: 600, letterSpacing: 1.2 }}
            >
              BENEFICIOS
            </Typography>
            <Typography
              component={motion.div}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              variant="h3"
              sx={{ mt: 1, mb: 2, fontWeight: 700 }}
            >
              ¿Por qué elegir MekaSys?
            </Typography>
            <Typography
              component={motion.div}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: '800px', mx: 'auto', fontWeight: 400 }}
            >
              Descubre cómo MekaSys puede transformar la gestión de tu taller mecánico
            </Typography>
          </Box>

          <Grid
            container
            spacing={4}
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.2,
                },
              },
            }}
          >
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      p: 3,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: `${benefit.color}15`,
                        color: benefit.color,
                        width: 80,
                        height: 80,
                        mb: 2,
                      }}
                    >
                      <benefit.icon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {benefit.description}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Features />

      {/* Testimonials */}
      <Box sx={{ py: 10, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              component={motion.div}
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              variant="overline"
              color="primary"
              sx={{ fontWeight: 600, letterSpacing: 1.2 }}
            >
              TESTIMONIOS
            </Typography>
            <Typography
              component={motion.div}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              variant="h3"
              sx={{ mt: 1, mb: 2, fontWeight: 700 }}
            >
              Lo que dicen nuestros clientes
            </Typography>
          </Box>

          <Grid
            container
            spacing={4}
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.2,
                },
              },
            }}
          >
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
                  }}
                >
                  <Card 
                    elevation={2}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      position: 'relative',
                      borderRadius: '12px',
                      p: 2,
                      background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, px: 3 }}>
                      <FormatQuoteIcon
                        sx={{
                          color: (theme) => theme.palette.primary.light,
                          fontSize: 40,
                          opacity: 0.3,
                          mb: 2
                        }}
                      />
                      <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', mb: 3 }}>
                        "{testimonial.quote}"
                      </Typography>
                      <Box sx={{ mt: 'auto' }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {testimonial.author}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonial.role}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <PricingPlans />

      {/* CTA Section */}
      <Box
        sx={{
          py: 10,
          bgcolor: 'primary.main',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            component={motion.div}
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            variant="h3"
            fontWeight={700}
            sx={{ mb: 3 }}
          >
            ¿Listo para transformar tu taller?
          </Typography>
          <Typography
            component={motion.div}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            variant="h6"
            sx={{ mb: 4, fontWeight: 400, opacity: 0.9 }}
          >
            Comienza hoy mismo tu prueba gratuita de 14 días. Sin tarjeta de crédito.
          </Typography>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Button
              variant="contained"
              color="secondary"
              size="large"
              sx={{ 
                py: 1.5, 
                px: 4, 
                fontWeight: 600, 
                fontSize: '1.1rem',
                boxShadow: '0 8px 20px rgba(245, 0, 87, 0.3)'
              }}
            >
              Comenzar prueba gratuita
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage; 